import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { VestingTask } from '../../types/vesting-tasks.types';
import { toNumber } from '../../utils/to-number.util';
import { useAppQuery } from '../use-app-query.hook';

export const useScenarioVestingTasks = ({
  scenarioId,
  numberOfFetchedItems = 100,
  currentPage = 1,
}: {
  currentPage?: number;
  numberOfFetchedItems?: number;
  scenarioId: string;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const { data, isLoading, refetch } = useAppQuery<
    { totalPages: number; totalRecords: number; vestingTasks: VestingTask[] },
    HttpException
  >({
    queryKey: [
      QueryKey.GET_SCENARIO_VESTING_TASKS,
      {
        page: currentPage,
        skip,
        numberOfFetchedItems,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.SCENARIOS, routePath: [scenarioId, 'grants-for-scenario'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
          },
        },
      ),
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SCENARIO_VESTING_TASKS] });
  }, [queryClient]);

  return {
    invalidateQuery,
    scenarioVestingTasks: data?.vestingTasks,
    totalPages: data?.totalPages,
    totalRecords: toNumber(data?.totalRecords),
    isLoading,
    refetch,
  };
};
